import { useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Lock } from "lucide-react";
import { startGoogleLogin, useAuth } from "../auth/AuthContext";

export default function AdminLogin() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;
  if (user?.is_admin) return <Navigate to="/admin/leads" replace />;

  const errorMsg = location.state?.error;
  const notAdmin = user && !user.is_admin;

  return (
    <div className="relative min-h-screen bg-brand-bg text-white flex items-center justify-center px-6 overflow-hidden">
      <div className="pointer-events-none absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-indigo-600/20 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 -right-32 w-[520px] h-[520px] rounded-full bg-indigo-500/15 blur-[140px]" />
      <div className="absolute inset-0 ring-grid opacity-30 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)]" />

      <motion.div
        data-testid="admin-login-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative w-full max-w-md glass rounded-2xl p-8 md:p-10 text-center"
      >
        <div className="mx-auto w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shadow-[0_0_28px_rgba(99,102,241,0.5)]">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <div className="text-xs uppercase tracking-[0.3em] text-indigo-300 mt-6">
          Admin Access
        </div>
        <h1 className="mt-3 font-display text-3xl md:text-4xl font-light tracking-tight">
          Sign in to the console
        </h1>
        <p className="mt-3 text-slate-300 text-sm">
          Restricted to approved administrator accounts only.
        </p>

        {errorMsg && (
          <div data-testid="admin-login-error" className="mt-5 text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            {errorMsg}
          </div>
        )}
        {notAdmin && (
          <div data-testid="admin-login-not-admin" className="mt-5 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
            Signed in as <span className="text-white">{user.email}</span> — this
            account is not authorised for admin access.
          </div>
        )}

        <button
          data-testid="admin-google-login"
          onClick={() => startGoogleLogin("/admin/leads")}
          className="mt-7 inline-flex items-center justify-center gap-3 w-full px-6 py-3.5 rounded-lg bg-white hover:bg-slate-100 text-slate-900 font-medium transition-all duration-300 shadow-lg"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continue with Google
        </button>

        <div className="mt-6 text-[11px] uppercase tracking-[0.25em] text-slate-500 flex items-center justify-center gap-2">
          <Lock className="w-3 h-3" /> Encrypted · Session-based · 7-day expiry
        </div>

        <a href="/" className="inline-block mt-6 text-xs text-slate-400 hover:text-white transition-colors">
          ← Back to site
        </a>
      </motion.div>
    </div>
  );
}
